import {
  buildTreeNsdiValueCategories,
  parseTreeValue,
} from '../../Blocks/VoyagerSearchPageBlock/Services/treeHelpers';
import type {
  FilterItem,
  FilterValueCategory,
} from '../../Blocks/VoyagerSearchPageBlock/models/search-result';

export type HierarchyTreeBaseOption = {
  value: string;
  label: string;
  depth: number;
};

export type HierarchyTreeBaseFacetUrlOptions = {
  solrSelectUrl: string;
  configId: string;
};

export const buildHierarchyTreeBaseFacetUrl = ({
  solrSelectUrl,
  configId,
}: HierarchyTreeBaseFacetUrlOptions): string => {
  const params = new URLSearchParams();
  params.set('basemap', 'ESRI Street Map');
  params.set('disp', configId);
  params.set('voyager.config.id', configId);
  params.set('rows', '0');
  params.set('facet', 'true');
  params.set('facet.mincount', '1');
  params.set('facet.limit', '-1');
  params.set('facet.field', 'tree_nsdi');
  params.set('f.tree_nsdi.facet.sort', 'index');
  params.set('wt', 'json');

  return `${solrSelectUrl}?${params.toString()}`;
};

export const buildHierarchyTreeBaseOptions = (
  values: Array<string | number> = [],
): HierarchyTreeBaseOption[] => {
  const filters: FilterItem[] = [];

  for (let i = 0; i < values.length; i += 2) {
    const rawValue = String(values[i] || '');
    const count = Number(values[i + 1] || 0);

    if (!rawValue || count <= 0) {
      continue;
    }

    const { depth, label, hierarchyPath } = parseTreeValue(rawValue);

    if (!depth || !label || !hierarchyPath) {
      continue;
    }

    filters.push({
      sectionId: 'tree_nsdi',
      id: rawValue,
      label,
      count,
      isChecked: false,
    });
  }

  const seen = new Set<string>();

  const toOption = (
    rawId: string,
    label: string,
  ): HierarchyTreeBaseOption | null => {
    const parsed = parseTreeValue(rawId);

    if (
      !parsed.depth ||
      !parsed.hierarchyPath ||
      seen.has(parsed.hierarchyPath)
    ) {
      return null;
    }

    seen.add(parsed.hierarchyPath);

    return {
      value: parsed.hierarchyPath,
      label: `${'\u00a0\u00a0'.repeat(Math.max(parsed.depth - 1, 0))}${label}`,
      depth: parsed.depth,
    };
  };

  const buildCategoryOptions = (
    category: FilterValueCategory,
  ): HierarchyTreeBaseOption[] => {
    const options: HierarchyTreeBaseOption[] = [];
    const categoryOption = toOption(category.id, category.label);

    if (categoryOption) {
      options.push(categoryOption);
    }

    category.children?.forEach((child) => {
      options.push(...buildCategoryOptions(child));
    });

    category.filters.forEach((filter) => {
      const option = toOption(filter.id, filter.label);
      if (option) {
        options.push(option);
      }
    });

    return options;
  };

  return buildTreeNsdiValueCategories(filters).flatMap(buildCategoryOptions);
};
